import { useEffect } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router';
import { AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

/**
 * RouteErrorPage — what a signed-in user sees when a routed screen throws.
 *
 * The app's ErrorBoundary wraps the router from outside, but React Router
 * catches render errors inside the route tree itself and, without an
 * errorElement, shows its developer page ("Unexpected Application Error",
 * a stack trace). The product audit's staging walk met exactly that on
 * /billing (finding S2, 2026-10-28). This element is set on the root route
 * in routes.tsx, so every child route falls back to it: a plain message,
 * the error's own words for the person reporting it, and two ways on.
 */
export function RouteErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();

  const status = isRouteErrorResponse(error) ? error.status : null;
  const notFound = status === 404;
  const detail = error instanceof Error
    ? error.message
    : isRouteErrorResponse(error)
      ? [error.status, error.statusText].filter(Boolean).join(' ')
      : typeof error === 'string'
        ? error
        : '';

  useEffect(() => {
    // Logged, never swallowed: this is the line to look for in the console.
    console.error('[RouteErrorPage] Route error:', error);
  }, [error]);

  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center p-8">
      <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
          <AlertCircle className="h-6 w-6 text-red-500" aria-hidden="true" />
        </div>
        <h1 className="mb-2">{notFound ? 'There is nothing at this address' : 'Something went wrong on this page'}</h1>
        <p className="text-muted-foreground">
          {notFound
            ? 'The link may be old, or the page may have moved.'
            : 'The rest of PathKit is unaffected, and nothing you saved elsewhere is lost. If this keeps happening, send us the line below.'}
        </p>
        {detail && !notFound && (
          <p className="mt-3 break-words rounded bg-gray-50 p-2 text-left text-muted-foreground" data-testid="route-error-detail">
            {detail}
          </p>
        )}
        <div className="mt-6 flex justify-center gap-3">
          <Button onClick={() => navigate('/dashboard')}>Back to dashboard</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>Reload this page</Button>
        </div>
      </div>
    </div>
  );
}
